import { z } from 'zod';
import { parseWebApiResponse } from './common';
const stringOrDateSchema = z.union([z.string(), z.date()]);
const nullableStringOrDateSchema = stringOrDateSchema.nullable().optional();
const nullableBooleanSchema = z.boolean().nullable().optional();
export const userPreferencesSchema = z
    .object({
    id: z.coerce.number().optional(),
    unreadType: z.string().nullable().optional(),
    isGhostUser: nullableBooleanSchema,
    isAutomatedSurveyUser: nullableBooleanSchema,
    isReadOnlyUser: nullableBooleanSchema,
    isDataOnlyUser: nullableBooleanSchema,
    isTasksOnlyUser: z.boolean().optional(),
    isUserAdmin: nullableBooleanSchema,
    canAccessAnnouncementCenter: nullableBooleanSchema,
    defaultToTasks: nullableBooleanSchema,
    sendSiteVisitEmailDigest: nullableBooleanSchema,
    escalateEmails: nullableBooleanSchema,
    sendEmailRollupDigests: nullableBooleanSchema,
    receivesPositiveMentionsDigest: nullableBooleanSchema,
    receivesMaintenanceSatisfactionDigest: nullableBooleanSchema,
    receivesEscalationsDigest: nullableBooleanSchema,
    receivesEscalationAlerts: nullableBooleanSchema,
    receivesWrapUpDigest: nullableBooleanSchema,
    residentEngagementAssociateId: z.coerce.number().nullable().optional(),
    userId: z.coerce.number().nullable().optional(),
    accountId: z.coerce.number().nullable().optional(),
    createdAt: nullableStringOrDateSchema,
    updatedAt: nullableStringOrDateSchema,
    deletedAt: nullableStringOrDateSchema,
})
    .passthrough();
export const userPreferencesResponseSchema = userPreferencesSchema.nullable();
export const announcementTemplateSchema = z
    .object({
    id: z.coerce.number(),
    shortcut: z.string().nullable().optional(),
    text: z.string().nullable().optional(),
    createdBy: z.coerce.number().optional(),
    accountId: z.coerce.number().nullable().optional(),
    createdAt: nullableStringOrDateSchema,
    updatedAt: nullableStringOrDateSchema,
    deletedAt: nullableStringOrDateSchema,
    isDefault: z.boolean().optional(),
})
    .passthrough();
export const announcementTemplatesListResponseSchema = z.array(announcementTemplateSchema);
export const announcementTemplateResponseSchema = announcementTemplateSchema;
const manageUserBaseSchema = z
    .object({
    id: z.coerce.number(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
})
    .passthrough();
export const manageUsersListRowSchema = manageUserBaseSchema.extend({
    deletedAt: nullableStringOrDateSchema,
    isAdmin: z.coerce.boolean().optional(),
    portfolios: z.array(z.string()).nullable().optional(),
    teams: z.array(z.string()).nullable().optional(),
    isUserAdmin: z.coerce.boolean().optional(),
    isReadOnlyUser: z.coerce.boolean().optional(),
    isDataOnlyUser: z.coerce.boolean().optional(),
    isTasksOnlyUser: z.coerce.boolean().optional(),
    isGhostUser: z.coerce.boolean().optional(),
    canAccessAnnouncementCenter: z.coerce.boolean().optional(),
    defaultToTasks: z.coerce.boolean().optional(),
});
export const manageUsersListResponseSchema = z.array(manageUsersListRowSchema);
export const manageUsersPortfolioOptionSchema = z
    .object({
    id: z.coerce.number(),
    name: z.string(),
    states: z.string().nullable().optional(),
})
    .passthrough();
export const manageUsersPortfolioListResponseSchema = z.array(manageUsersPortfolioOptionSchema);
export const manageUsersPropertyContactNumberSchema = z
    .object({
    portfolioId: z.coerce.number(),
    propertyName: z.string(),
    phoneNumber: z.string().nullable().optional(),
})
    .passthrough();
export const manageUsersPropertyContactNumbersResponseSchema = z.array(manageUsersPropertyContactNumberSchema);
export const manageUsersPropertyUserAccessRowSchema = z
    .object({
    portfolioId: z.coerce.number(),
    propertyName: z.string(),
    userId: z.coerce.number(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
})
    .passthrough();
export const manageUsersPropertyUserAccessResponseSchema = z.array(manageUsersPropertyUserAccessRowSchema);
export const manageUsersLoginHistoryRowSchema = z
    .object({
    userId: z.coerce.number(),
    firstName: z.string().nullable().optional(),
    accountId: z.coerce.number(),
    email: z.string().nullable().optional(),
    isDefaultPassword: z.coerce.boolean(),
    accountCreated: nullableStringOrDateSchema,
    lastPasswordUpdate: nullableStringOrDateSchema,
    isDataOnly: z.coerce.boolean().nullable().optional(),
    isReadOnly: z.coerce.boolean().nullable().optional(),
    isUserAdmin: z.coerce.boolean().nullable().optional(),
    lastActivity: nullableStringOrDateSchema,
    accountAgeInDays: z.coerce.number().nullable().optional(),
    daysSinceLastActivity: z.coerce.number().nullable().optional(),
})
    .passthrough();
export const manageUsersLoginHistoryResponseSchema = z.array(manageUsersLoginHistoryRowSchema);
export const manageUsersContactGroupSchema = z
    .object({
    accountName: z.string(),
    portfolioId: z.coerce.number(),
    portfolioName: z.string(),
    frontContactListId: z.string(),
    frontContactListName: z.string(),
    inheritToDescendants: z.boolean(),
})
    .passthrough();
export const manageUsersContactGroupsResponseSchema = z.array(manageUsersContactGroupSchema);
export const manageUsersFrontContactSchema = z
    .object({
    id: z.string().nullable().optional(),
    name: z.string(),
    email: z.string(),
})
    .passthrough();
export const manageUsersContactGroupContactsResponseSchema = z.array(manageUsersFrontContactSchema);
export const manageUsersCreateResponseSchema = z
    .object({
    user: manageUserBaseSchema.extend({
        accountId: z.coerce.number(),
    }),
    welcomeEmailSent: z.coerce.boolean(),
    welcomeEmailRequested: z.coerce.boolean().optional(),
    message: z.string(),
})
    .strip();
export const manageUsersOkResponseSchema = z
    .object({
    ok: z.coerce.boolean(),
    emailSent: z.coerce.boolean().optional(),
})
    .passthrough();
const manageUsersFrontSyncSchema = z
    .object({
    succeeded: z.array(z.coerce.number()),
    failed: z.array(z.coerce.number()),
    skipped: z.array(z.coerce.number()).optional(),
})
    .strip();
export const manageUsersBulkAssignResponseSchema = z
    .object({
    ok: z.literal(true),
    assigned: z.array(z.coerce.number()),
    teamsAssigned: z.array(z.coerce.number()),
    frontSync: manageUsersFrontSyncSchema,
})
    .strip();
export const manageUsersBulkRemoveResponseSchema = z
    .object({
    ok: z.literal(true),
    removed: z.array(z.coerce.number()),
    frontSync: manageUsersFrontSyncSchema,
})
    .strip();
const internalManagedUserSchema = z
    .object({
    id: z.coerce.number(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    homeAccountId: z.coerce.number(),
    homeAccountName: z.string(),
    isAdmin: z.boolean(),
})
    .strip();
const internalDirectPropertyAssignmentSchema = z
    .object({
    id: z.coerce.number(),
    name: z.string(),
    accountId: z.coerce.number().nullable(),
    accountName: z.string().nullable(),
    isActive: z.boolean(),
    isLeaf: z.boolean(),
})
    .strip();
const internalPropertyOptionSchema = z
    .object({
    id: z.coerce.number(),
    name: z.string(),
    accountId: z.coerce.number(),
    accountName: z.string(),
})
    .strip();
export const internalManagedUserLookupResponseSchema = z
    .object({
    ok: z.literal(true),
    user: internalManagedUserSchema,
    assignments: z.array(internalDirectPropertyAssignmentSchema),
})
    .strip();
export const internalManagedUserSearchResponseSchema = z.array(internalManagedUserSchema);
export const internalPropertySearchResponseSchema = z.array(internalPropertyOptionSchema);
export const internalPropertyChangeResponseSchema = z
    .object({
    ok: z.literal(true),
    mutationId: z.string().uuid(),
    added: z.array(z.coerce.number()),
    removed: z.array(z.coerce.number()),
    frontSync: manageUsersFrontSyncSchema,
})
    .strip();
export const manageUsersUserPreferencesResponseSchema = userPreferencesResponseSchema;
export function parseUserPreferencesResponse(data) {
    return parseWebApiResponse(userPreferencesResponseSchema, data, 'GET/PUT /api/user/preferences/all');
}
export function parseAnnouncementTemplatesListResponse(data) {
    return parseWebApiResponse(announcementTemplatesListResponseSchema, data, 'GET /api/announcements/templates');
}
export function parseAnnouncementTemplateResponse(data) {
    return parseWebApiResponse(announcementTemplateResponseSchema, data, 'POST/PUT /api/announcements/templates');
}
export function parseManageUsersListResponse(data) {
    return parseWebApiResponse(manageUsersListResponseSchema, data, 'GET /api/manage-users/*');
}
export function parseManageUsersPortfolioListResponse(data) {
    return parseWebApiResponse(manageUsersPortfolioListResponseSchema, data, 'GET /api/manage-users/users/:userId/*-portfolios');
}
export function parseManageUsersPropertyContactNumbersResponse(data) {
    return parseWebApiResponse(manageUsersPropertyContactNumbersResponseSchema, data, 'GET /api/manage-users/property-contact-numbers');
}
export function parseManageUsersPropertyUserAccessResponse(data) {
    return parseWebApiResponse(manageUsersPropertyUserAccessResponseSchema, data, 'GET /api/manage-users/property-user-access');
}
export function parseManageUsersLoginHistoryResponse(data) {
    return parseWebApiResponse(manageUsersLoginHistoryResponseSchema, data, 'GET /api/manage-users/login-history');
}
export function parseManageUsersContactGroupsResponse(data) {
    return parseWebApiResponse(manageUsersContactGroupsResponseSchema, data, 'GET /api/manage-users/contact-groups');
}
export function parseManageUsersContactGroupContactsResponse(data) {
    return parseWebApiResponse(manageUsersContactGroupContactsResponseSchema, data, 'GET /api/manage-users/contact-groups/:contactGroupId/contacts');
}
export function parseManageUsersCreateResponse(data) {
    return parseWebApiResponse(manageUsersCreateResponseSchema, data, 'POST /api/manage-users/users');
}
export function parseManageUsersOkResponse(data) {
    return parseWebApiResponse(manageUsersOkResponseSchema, data, 'manage-users mutation');
}
export function parseManageUsersBulkAssignResponse(data) {
    return parseWebApiResponse(manageUsersBulkAssignResponseSchema, data, 'POST /api/manage-users/bulk-portfolio-assign');
}
export function parseManageUsersBulkRemoveResponse(data) {
    return parseWebApiResponse(manageUsersBulkRemoveResponseSchema, data, 'POST /api/manage-users/bulk-portfolio-remove');
}
export function parseManageUsersUserPreferencesResponse(data) {
    return parseWebApiResponse(manageUsersUserPreferencesResponseSchema, data, 'GET/PUT /api/manage-users/users/:userId/preferences');
}
export function parseInternalManagedUserLookupResponse(data) {
    return parseWebApiResponse(internalManagedUserLookupResponseSchema, data, 'GET /api/manage-users/internal/users/lookup');
}
export function parseInternalManagedUserSearchResponse(data) {
    return parseWebApiResponse(internalManagedUserSearchResponseSchema, data, 'GET /api/manage-users/internal/users/search');
}
export function parseInternalPropertySearchResponse(data) {
    return parseWebApiResponse(internalPropertySearchResponseSchema, data, 'GET /api/manage-users/internal/properties/search');
}
export function parseInternalPropertyChangeResponse(data) {
    return parseWebApiResponse(internalPropertyChangeResponseSchema, data, 'PATCH /api/manage-users/internal/users/:userId/properties');
}
